/**
 * CombinedFootingPage — combined footing under two columns.
 *
 * Center: plan view with both columns + bottom envelope of M(x) and V(x)
 * diagrams underneath, plus a 2D/3D toggle.
 * Right: results (bearing, envelope, shear, punching, flexion).
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCombinedStore } from '@/features/zapatas_combined/store';
import { useCombinedAnalysis } from '@/features/zapatas_combined/useAnalysis';
import { useExportCombined } from '@/features/zapatas_combined/useExport';
import { Combined3DView } from '@/features/zapatas_combined/Combined3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { CombinedAnalyzeRequest, RebarDiam } from '@/features/zapatas_combined/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

export function CombinedFootingPage({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const request = useCombinedStore((s) => s.request);
  const setSection = useCombinedStore((s) => s.setSection);
  const setMaterials = useCombinedStore((s) => s.setMaterials);
  const setSoil = useCombinedStore((s) => s.setSoil);
  const setLoads = useCombinedStore((s) => s.setLoads);
  const setReinforcement = useCombinedStore((s) => s.setReinforcement);
  const loadRequest = useCombinedStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data && designQuery.data.subtype === 'combined') {
      loadRequest(designQuery.data.payload as unknown as CombinedAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const [saveOpen, setSaveOpen] = useState(false);
  const { data, isFetching, isError, error } = useCombinedAnalysis(request);
  const exporter = useExportCombined();
  const [view, setView] = useState<'2D' | '3D'>('2D');

  const matchingDesign =
    designQuery.data && designQuery.data.subtype === 'combined' ? designQuery.data : null;

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <header className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}>
          <Link to="/app/footings" className="flex items-center gap-1 text-[11px]"
            style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} /><span>Hub zapatas</span>
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Software E.060 + E.050
            </div>
            <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
              Diseño de zapata combinada (2 columnas)
            </div>
          </div>
        </header>
      )}

      <DesignToolbar
        status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
        statusLabel={isError ? `Error: ${String(error)}` : undefined}
        onExportPdf={() => exporter.trigger('pdf', request)}
        onExportXlsx={() => exporter.trigger('xlsx', request)}
        busyExport={exporter.busy}
        onSave={() => setSaveOpen(true)}
        isUpdate={!!matchingDesign}
      />
      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="zapatas"
        subtype="combined"
        payload={request as unknown as Record<string, unknown>}
        existing={matchingDesign}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          <Group title="Geometría">
            <NumberRow label="L (cm) — largo" value={request.section.L} onChange={(v) => setSection({ L: v })} step={10} />
            <NumberRow label="B (cm) — ancho" value={request.section.B} onChange={(v) => setSection({ B: v })} step={5} />
            <NumberRow label="Hf (cm)" value={request.section.Hf} onChange={(v) => setSection({ Hf: v })} step={5} />
            <NumberRow label="df (cm)" value={request.section.df} onChange={(v) => setSection({ df: v })} step={10} />
          </Group>

          <Group title="Columna 1">
            <NumberRow label="bc1 (cm)" value={request.section.bc1} onChange={(v) => setSection({ bc1: v })} step={5} />
            <NumberRow label="hc1 (cm)" value={request.section.hc1} onChange={(v) => setSection({ hc1: v })} step={5} />
            <NumberRow label="x1 — centro desde izq. (cm)" value={request.section.x1} onChange={(v) => setSection({ x1: v })} step={5} />
            <NumberRow label="P1 servicio (kgf)" value={request.loads.P1} onChange={(v) => setLoads({ P1: v })} step={1000} />
            <NumberRow label="Pu1 factor (kgf)" value={request.loads.Pu1} onChange={(v) => setLoads({ Pu1: v })} step={1000} />
          </Group>

          <Group title="Columna 2">
            <NumberRow label="bc2 (cm)" value={request.section.bc2} onChange={(v) => setSection({ bc2: v })} step={5} />
            <NumberRow label="hc2 (cm)" value={request.section.hc2} onChange={(v) => setSection({ hc2: v })} step={5} />
            <NumberRow label="x2 — centro desde izq. (cm)" value={request.section.x2} onChange={(v) => setSection({ x2: v })} step={5} />
            <NumberRow label="P2 servicio (kgf)" value={request.loads.P2} onChange={(v) => setLoads({ P2: v })} step={1000} />
            <NumberRow label="Pu2 factor (kgf)" value={request.loads.Pu2} onChange={(v) => setLoads({ Pu2: v })} step={1000} />
          </Group>

          <Group title="Materiales y suelo">
            <NumberRow label="f'c" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} step={10} />
            <NumberRow label="fy" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} step={100} />
            <NumberRow label="Recubrim." value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
            <NumberRow label="σ_adm (kgf/cm²)" value={request.soil.sigma_adm} onChange={(v) => setSoil({ sigma_adm: v })} step={0.1} />
          </Group>

          <Group title="Refuerzo">
            <SelectRow label="Ø abajo (long.)" value={request.reinforcement.diam_bot_long}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_bot_long: v as RebarDiam })} />
            <NumberRow label="N abajo (long.)" value={request.reinforcement.n_bot_long}
              onChange={(v) => setReinforcement({ n_bot_long: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø arriba (long.)" value={request.reinforcement.diam_top_long}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_top_long: v as RebarDiam })} />
            <NumberRow label="N arriba (long.)" value={request.reinforcement.n_top_long}
              onChange={(v) => setReinforcement({ n_top_long: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø transversal" value={request.reinforcement.diam_trans}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_trans: v as RebarDiam })} />
            <NumberRow label="s transversal (cm)" value={request.reinforcement.s_trans}
              onChange={(v) => setReinforcement({ s_trans: v })} step={1} />
          </Group>
        </aside>

        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={view === '3D' ? 'Vista 3D — zapata combinada' : 'Planta + envolventes V(x) y M(x)'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button key={m} type="button" onClick={() => setView(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{ background: view === m ? 'var(--accent)' : 'transparent',
                          color: view === m ? 'var(--surface-base)' : 'var(--text-secondary)' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view === '3D' ? (
                  <div style={{ width: 880, height: 500 }}>
                    <Combined3DView
                      L={request.section.L} B={request.section.B}
                      Hf={request.section.Hf} df={request.section.df}
                      bc1={request.section.bc1} hc1={request.section.hc1} x1={request.section.x1}
                      bc2={request.section.bc2} hc2={request.section.hc2} x2={request.section.x2}
                      cover={request.materials.cover}
                      diam_bot_long={request.reinforcement.diam_bot_long}
                      n_bot_long={request.reinforcement.n_bot_long}
                      diam_top_long={request.reinforcement.diam_top_long}
                      n_top_long={request.reinforcement.n_top_long}
                      diam_trans={request.reinforcement.diam_trans}
                      s_trans={request.reinforcement.s_trans}
                    />
                  </div>
                ) : (
                  <CombinedPlanAndEnvelope
                    L={request.section.L} B={request.section.B}
                    bc1={request.section.bc1} hc1={request.section.hc1} x1={request.section.x1}
                    bc2={request.section.bc2} hc2={request.section.hc2} x2={request.section.x2}
                    x_arr={data.envelope.x} V_arr={data.envelope.V} M_arr={data.envelope.M}
                    x_centroid={data.geometry.load_centroid}
                  />
                )}
              </DiagramCard>

              {view === '2D' && (
                <DiagramCard title="Elevación — corte longitudinal por las dos columnas">
                  <CombinedElevationDiagram
                    L={request.section.L}
                    Hf={request.section.Hf}
                    df={request.section.df}
                    bc1={request.section.bc1} x1={request.section.x1}
                    bc2={request.section.bc2} x2={request.section.x2}
                    sigma_max={data.bearing.sigma_max}
                    sigma_min={data.bearing.sigma_min}
                    sigma_adm={data.bearing.sigma_adm_eff}
                  />
                </DiagramCard>
              )}
            </div>
          )}
        </main>

        <aside className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          {data && (
            <>
              <ResultCard title="Geometría">
                <KV label="Área" value={`${(data.geometry.A / 10000).toFixed(2)} m²`} />
                <KV label="Centroide de cargas" value={`${data.geometry.load_centroid.toFixed(1)} cm`} />
                <KV label="Centro geom. (L/2)" value={`${data.geometry.geometric_center.toFixed(1)} cm`} />
                <KV label="Excentricidad" value={`${data.geometry.eccentricity.toFixed(2)} cm`} mono />
                <KV label="Voladizo izq." value={`${data.geometry.cantilever_left.toFixed(1)} cm`} />
                <KV label="Voladizo der." value={`${data.geometry.cantilever_right.toFixed(1)} cm`} />
                <KV label="d_long efectivo" value={`${data.geometry.d_eff_long.toFixed(1)} cm`} />
              </ResultCard>

              <ResultCard title="Presión sobre el suelo">
                <KV label="P_total" value={`${(data.bearing.P_total / 1000).toFixed(2)} tonf`} />
                <KV label="σ_max" value={`${data.bearing.sigma_max.toFixed(3)} kgf/cm²`} mono />
                <KV label="σ_min" value={`${data.bearing.sigma_min.toFixed(3)} kgf/cm²`} mono />
                <KV label="σ_adm" value={`${data.bearing.sigma_adm_eff.toFixed(3)} kgf/cm²`} />
                <div className="mt-1 text-[10px]" style={{ color: data.bearing.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.bearing.ok ? '✓ ' : '✗ '}{data.bearing.note}
                </div>
              </ResultCard>

              <ResultCard title="Envolvente longitudinal">
                <KV label="V max |abs|" value={`${(data.envelope.Vmax_abs / 1000).toFixed(2)} tonf`} />
                <KV label="M+ máx (entre cols)" value={`${(data.envelope.M_pos_max / 1e5).toFixed(2)} tonf·m en x=${data.envelope.x_M_pos_max.toFixed(0)} cm`} />
                <KV label="M− máx (voladizos)" value={`${(data.envelope.M_neg_max / 1e5).toFixed(2)} tonf·m en x=${data.envelope.x_M_neg_max.toFixed(0)} cm`} />
              </ResultCard>

              <ResultCard title="Cortante 1D">
                <KV label="x crítico" value={`${data.one_way_shear.critical_x.toFixed(1)} cm`} />
                <KV label="Vu" value={`${(data.one_way_shear.Vu / 1000).toFixed(2)} tonf`} />
                <KV label="φVc" value={`${(data.one_way_shear.phi_Vc / 1000).toFixed(2)} tonf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.one_way_shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.one_way_shear.ok ? '✓ OK' : '✗ REVISAR'}
                </div>
              </ResultCard>

              <ResultCard title="Punzonado">
                <KV label="Col 1 — Vu" value={`${(data.punching.col1.Vu_p / 1000).toFixed(2)} tonf`} />
                <KV label="Col 1 — φVc" value={`${(data.punching.col1.phi_Vc / 1000).toFixed(2)} tonf`} />
                <KV label="Col 2 — Vu" value={`${(data.punching.col2.Vu_p / 1000).toFixed(2)} tonf`} />
                <KV label="Col 2 — φVc" value={`${(data.punching.col2.phi_Vc / 1000).toFixed(2)} tonf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.punching.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.punching.ok ? '✓ OK ambas' : '✗ REVISAR — aumentar Hf'}
                </div>
              </ResultCard>

              <ResultCard title="Flexión longitudinal">
                <KV label="As bot. req." value={`${data.flexion_long.As_pos_req.toFixed(2)} cm²`} />
                <KV label="As bot. prov." value={`${data.flexion_long.As_pos_provided.toFixed(2)} cm²`} />
                <KV label="As top. req." value={`${data.flexion_long.As_neg_req.toFixed(2)} cm²`} />
                <KV label="As top. prov." value={`${data.flexion_long.As_neg_provided.toFixed(2)} cm²`} />
                <KV label="Acero bot." value={data.flexion_long.bot_label} mono />
                <KV label="Acero top." value={data.flexion_long.top_label} mono />
                {(() => {
                  // Rho_bot = As_pos_provided / (B · d_long), rho_min = As_min / (B · Hf)
                  const B = request.section.B;
                  const d = data.geometry.d_eff_long;
                  const Hf = request.section.Hf;
                  const rho_bot = data.flexion_long.As_pos_provided / (B * d);
                  const rho_min_bot = data.flexion_long.As_min / (B * Hf);
                  const rho_top = data.flexion_long.As_neg_provided / (B * d);
                  return (
                    <div className="mt-2 space-y-3">
                      <RhoBar
                        title="ρ inferior (longitudinal) — As_bot / (B · d)"
                        rho={rho_bot}
                        rho_min={rho_min_bot}
                        rho_max={0.04}
                        hideAdvice
                      />
                      <RhoBar
                        title="ρ superior (longitudinal) — As_top / (B · d)"
                        rho={rho_top}
                        rho_min={rho_min_bot}
                        rho_max={0.04}
                        hideAdvice
                      />
                    </div>
                  );
                })()}
              </ResultCard>

              <ResultCard title="Flexión transversal">
                <KV label="Mu/m" value={`${(data.flexion_trans.Mu_per_m / 1e5).toFixed(3)} tonf·m/m`} />
                <KV label="As req./m" value={`${data.flexion_trans.As_required_per_m.toFixed(2)} cm²/m`} />
                <KV label="As prov./m" value={`${data.flexion_trans.As_provided_per_m.toFixed(2)} cm²/m`} />
                <KV label="Acero" value={data.flexion_trans.label} mono />
                <div className="mt-1 text-[10px]" style={{ color: data.flexion_trans.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.flexion_trans.ok ? '✓ OK' : '✗ Densificar'}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span className="mt-0.5" style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}>
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>{v.label}</span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input type="number" value={value} step={step}
        onChange={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v)) onChange(v); }}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }} />
    </label>
  );
}
function SelectRow({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {children}
    </div>
  );
}
function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
function DiagramCard({ title, children, toolbar }: { title: string; children: React.ReactNode; toolbar?: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3" style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  PLAN + V/M ENVELOPES                                                    */
/* ====================================================================== */

function CombinedPlanAndEnvelope({
  L, B, bc1, hc1, x1, bc2, hc2, x2,
  x_arr, V_arr, M_arr, x_centroid,
}: {
  L: number; B: number;
  bc1: number; hc1: number; x1: number;
  bc2: number; hc2: number; x2: number;
  x_arr: number[]; V_arr: number[]; M_arr: number[];
  x_centroid: number;
}) {
  const W = 880;
  const PAD = 40;
  const drawW = W - 2 * PAD;
  const scaleX = drawW / L;

  // Plan view dimensions
  const planH = B * scaleX;
  const planY = 40;
  const xL = PAD;
  const xR = xL + drawW;

  // Envelope chart heights
  const envH = 90;
  const yV0 = planY + planH + 60;     // V envelope baseline
  const yM0 = yV0 + envH + 50;        // M envelope baseline
  const totalH = yM0 + envH + 40;

  // Scale envelopes
  const Vmax = Math.max(1, ...V_arr.map(Math.abs));
  const Mmax = Math.max(1, ...M_arr.map(Math.abs));
  const Vy = (v: number) => yV0 - (v / Vmax) * (envH / 2);
  const My = (m: number) => yM0 - (m / Mmax) * (envH / 2);
  const Xat = (x: number) => xL + x * scaleX;

  const Vpath = V_arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${Xat(x_arr[i]).toFixed(1)} ${Vy(v).toFixed(1)}`).join(' ');
  const Mpath = M_arr.map((m, i) => `${i === 0 ? 'M' : 'L'} ${Xat(x_arr[i]).toFixed(1)} ${My(m).toFixed(1)}`).join(' ');

  return (
    <svg width={W} height={totalH} viewBox={`0 0 ${W} ${totalH}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-comb" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Plan view */}
      <rect x={xL} y={planY} width={drawW} height={planH}
        fill="url(#hatch-comb)" stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Column 1 */}
      <rect x={Xat(x1) - bc1 * scaleX / 2} y={planY + planH / 2 - hc1 * scaleX / 2}
        width={bc1 * scaleX} height={hc1 * scaleX}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.4" />
      <text x={Xat(x1)} y={planY + planH / 2 + 3} textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill="var(--accent)" style={{ fontWeight: 700 }}>C1</text>
      {/* Column 2 */}
      <rect x={Xat(x2) - bc2 * scaleX / 2} y={planY + planH / 2 - hc2 * scaleX / 2}
        width={bc2 * scaleX} height={hc2 * scaleX}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.4" />
      <text x={Xat(x2)} y={planY + planH / 2 + 3} textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill="var(--accent)" style={{ fontWeight: 700 }}>C2</text>

      {/* Load centroid marker */}
      <line x1={Xat(x_centroid)} y1={planY - 8} x2={Xat(x_centroid)} y2={planY + planH + 8}
        stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.4" strokeDasharray="4 3" />
      <text x={Xat(x_centroid)} y={planY - 10} textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill="var(--brand-violet, #8B5CF6)">centroide cargas</text>

      {/* Geometric center */}
      <line x1={xL + drawW / 2} y1={planY - 8} x2={xL + drawW / 2} y2={planY + planH + 8}
        stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 3" />
      <text x={xL + drawW / 2} y={planY + planH + 18} textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill="var(--text-muted)">L/2</text>

      <text x={xL + drawW / 2} y={planY + planH + 30} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        L = {L} cm  ·  B = {B} cm
      </text>

      {/* V envelope */}
      <line x1={xL} y1={yV0} x2={xR} y2={yV0} stroke="var(--text-muted)" strokeWidth="0.8" />
      <path d={Vpath} fill="none" stroke="var(--brand-alt, #3B82F6)" strokeWidth="1.6" />
      <text x={xL} y={yV0 - envH / 2 - 4} fontSize="10" fontFamily="monospace" fill="var(--brand-alt, #3B82F6)" style={{ fontWeight: 700 }}>
        V(x) [V_max = {(Vmax / 1000).toFixed(2)} tonf]
      </text>

      {/* M envelope */}
      <line x1={xL} y1={yM0} x2={xR} y2={yM0} stroke="var(--text-muted)" strokeWidth="0.8" />
      <path d={Mpath} fill="none" stroke="var(--accent)" strokeWidth="1.8" />
      <text x={xL} y={yM0 - envH / 2 - 4} fontSize="10" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
        M(x) [M_max = {(Mmax / 1e5).toFixed(2)} tonf·m]
      </text>
      <text x={xL} y={yM0 + envH / 2 + 14} fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        + entre columnas (acero abajo)   ·   − en voladizos (acero arriba)
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  ELEVATION — longitudinal cut through the two columns                    */
/* ====================================================================== */

function CombinedElevationDiagram({
  L, Hf, df, bc1, x1, bc2, x2, sigma_max, sigma_min, sigma_adm,
}: {
  L: number; Hf: number; df: number;
  bc1: number; x1: number; bc2: number; x2: number;
  sigma_max: number; sigma_min: number; sigma_adm: number;
}) {
  const W = 880;
  const PAD = 50;
  const drawW = W - 2 * PAD;
  const scaleX = drawW / L;
  const scaleY = Math.min(scaleX, 100 / Math.max(Hf, 1));
  const wpx = L * scaleX;
  const xL = (W - wpx) / 2;
  const yGrade = 56;
  const ySlabTop = yGrade + (df - Hf) * scaleY;
  const ySlabBot = ySlabTop + Hf * scaleY;
  const H = ySlabBot + 120;

  // Column heights above grade (~80 cm)
  const colTop = yGrade - 60;

  // Stress trapezoid below slab
  const stress_H_max = 50;
  const sMax = Math.max(sigma_max, sigma_adm, 1e-6);
  const smaxPx = (sigma_max / sMax) * stress_H_max;
  const sminPx = Math.max(sigma_min, 0) / sMax * stress_H_max;
  const yStress = ySlabBot + 18;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-comb-elev" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.18" strokeWidth="1" />
        </pattern>
        <pattern id="soil-comb-elev" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#7C2D12" fillOpacity="0.10" />
          <circle cx="2" cy="2" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
          <circle cx="6" cy="6" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
        </pattern>
      </defs>

      {/* Soil */}
      <rect x={xL - 40} y={yGrade} width={wpx + 80} height={ySlabBot - yGrade + 10} fill="url(#soil-comb-elev)" />
      <line x1={xL - 50} y1={yGrade} x2={xL + wpx + 50} y2={yGrade} stroke="#7C2D12" strokeWidth="1.4" />
      <text x={xL - 50} y={yGrade - 4} fontSize="10" fontFamily="monospace" fill="#7C2D12">NPT</text>

      {/* Footing slab */}
      <rect x={xL} y={ySlabTop} width={wpx} height={Hf * scaleY}
        fill="url(#hatch-comb-elev)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Column 1 */}
      {(() => {
        const colW = bc1 * scaleX;
        const colCx = xL + x1 * scaleX;
        return (
          <g>
            <rect x={colCx - colW / 2} y={colTop} width={colW} height={ySlabTop - colTop}
              fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.3" />
            <text x={colCx} y={colTop - 5} textAnchor="middle" fontSize="10"
              fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
              C1 ({bc1} cm)
            </text>
          </g>
        );
      })()}

      {/* Column 2 */}
      {(() => {
        const colW = bc2 * scaleX;
        const colCx = xL + x2 * scaleX;
        return (
          <g>
            <rect x={colCx - colW / 2} y={colTop} width={colW} height={ySlabTop - colTop}
              fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.3" />
            <text x={colCx} y={colTop - 5} textAnchor="middle" fontSize="10"
              fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
              C2 ({bc2} cm)
            </text>
          </g>
        );
      })()}

      {/* Stress trapezoid */}
      <polygon
        points={`${xL},${yStress} ${xL + wpx},${yStress} ${xL + wpx},${yStress - sminPx} ${xL},${yStress - smaxPx}`}
        fill="rgba(220,38,38,0.18)" stroke="var(--danger)" strokeWidth="1.3"
      />
      <text x={xL - 4} y={yStress - smaxPx - 4} textAnchor="end" fontSize="10"
        fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        σ_max = {sigma_max.toFixed(3)}
      </text>
      <text x={xL + wpx + 4} y={yStress - sminPx - 4} fontSize="10"
        fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        σ_min = {sigma_min.toFixed(3)}
      </text>
      {/* σ_adm reference */}
      {(() => {
        const padmPx = (sigma_adm / sMax) * stress_H_max;
        return (
          <g>
            <line x1={xL} y1={yStress - padmPx} x2={xL + wpx} y2={yStress - padmPx}
              stroke="var(--success)" strokeWidth="1" strokeDasharray="3 2" />
            <text x={xL + wpx + 4} y={yStress - padmPx + 3} fontSize="9.5"
              fontFamily="monospace" fill="var(--success)">σ_adm = {sigma_adm.toFixed(3)}</text>
          </g>
        );
      })()}

      {/* Dimensions */}
      <text x={W / 2} y={ySlabBot + 12} textAnchor="middle" fontSize="11"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        L = {L} cm  ·  Hf = {Hf} cm  ·  df = {df} cm
      </text>
    </svg>
  );
}
